import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2, Banknote } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin: edit configurable FSI cost rates (TDR scrutiny, demolition waste,
 * lift machine room, C2 zone, plus legacy scrutiny / RADA-RODA per sqm).
 *
 * Any change here is picked up by calculations.js the next time the user opens
 * a project (it fetches /api/fsi-config). No code redeploy required.
 */
const DEFAULTS = {
  scrutiny_per_sqm: 5,
  rada_roda_per_sqm: 20,
  tdr_scrutiny_per_sqm: 30,
  demolition_waste_area_percent: 5,
  demolition_waste_per_sqm: 227,
  lift_machine_room_area_per_lift_sqm: 8,
  lift_machine_room_land_rr_percent: 60,
  c2_conversion_plot_percent: 40,
  c2_additional_construction_plot_percent: 50,
  water_line_per_sqrt_plot: 3640,
};

const FIELDS = [
  { group: "Universal FSI cost addons (apply to all projects)" },
  { key: "tdr_scrutiny_per_sqm", label: "TDR Scrutiny Charges (₹ per sqm of TDR area)", unit: "₹/sqm" },
  { key: "demolition_waste_area_percent", label: "Construction Demolition Waste — area factor", unit: "% of total FSI area" },
  { key: "demolition_waste_per_sqm", label: "Construction Demolition Waste — rate", unit: "₹/sqm" },
  { key: "lift_machine_room_area_per_lift_sqm", label: "Lift Machine Room — area per lift", unit: "sqm/lift" },
  { key: "lift_machine_room_land_rr_percent", label: "Lift Machine Room — Land RR multiplier", unit: "% of Land RR" },
  { key: "water_line_per_sqrt_plot", label: "Water Line Charges — rate", unit: "₹ × √(plot sqm)" },

  { group: "C2 Zone surcharges (commercial / mixed-use only)" },
  { key: "c2_conversion_plot_percent", label: "Conversion charges — plot factor", unit: "% of plot area × land RR" },
  { key: "c2_additional_construction_plot_percent", label: "Additional construction area", unit: "% of plot area" },

  { group: "Legacy sanction rates (apply to non-MHADA, non-narrow-road)" },
  { key: "scrutiny_per_sqm", label: "Scrutiny charges (existing)", unit: "₹/sqm" },
  { key: "rada_roda_per_sqm", label: "RADA / RODA charges (existing)", unit: "₹/sqm" },
];

const FSICostRatesPanel = () => {
  const [fullConfig, setFullConfig] = useState(null);
  const [rates, setRates] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/fsi-config`, { withCredentials: true });
      setFullConfig(res.data);
      setRates({ ...DEFAULTS, ...(res.data?.fsi_costs || {}) });
    } catch (err) {
      toast.error("Failed to load cost rates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleChange = (key, value) => {
    setRates(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...fullConfig, fsi_costs: rates };
      await axios.put(`${API}/api/fsi-config`, payload, { withCredentials: true });
      toast.success("Cost rates saved. Users will pick up changes on next project open.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    if (!window.confirm("Reset all cost rates to factory defaults?")) return;
    setRates(DEFAULTS);
    toast.info("Reset to defaults (click Save to persist)");
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <Card data-testid="fsi-cost-rates-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Banknote className="h-5 w-5 text-primary" />
          Cost Rates Configuration
        </CardTitle>
        <CardDescription>
          Edit the rates used by the feasibility calculator. Changes apply globally to all users on next project load (no redeploy).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {FIELDS.map((f, idx) => {
          if (f.group) {
            return (
              <div key={`g-${idx}`} className="border-t pt-3 first:border-t-0 first:pt-0">
                <h4 className="text-sm font-semibold text-primary">{f.group}</h4>
              </div>
            );
          }
          return (
            <div key={f.key} className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-2 sm:gap-4 items-center">
              <div>
                <Label className="text-sm">{f.label}</Label>
                <p className="text-[11px] text-muted-foreground">Default: {DEFAULTS[f.key]} {f.unit}</p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.01"
                  value={rates[f.key] ?? ""}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  className="rounded-sm"
                  data-testid={`rate-${f.key}`}
                />
                <span className="text-xs text-muted-foreground whitespace-nowrap">{f.unit}</span>
              </div>
            </div>
          );
        })}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-3 border-t">
          <Button variant="outline" size="sm" onClick={handleResetToDefaults} className="rounded-sm gap-2" data-testid="reset-rates-btn">
            <RotateCcw className="h-4 w-4" /> Reset to Defaults
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-sm gap-2" data-testid="save-rates-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Rates
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FSICostRatesPanel;
